// src/features/pdf/components/ToolActionArea.tsx
import React, { useState, ReactNode } from 'react'; // Import ReactNode
import { Box, Button, Typography, Stack, CircularProgress, Alert, Collapse, List, ListItem, ListItemIcon, ListItemText, IconButton, Select, MenuItem, FormControl, InputLabel, Paper, ListItemButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DownloadIcon from '@mui/icons-material/Download';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { searchableTools } from '../../../config/menuConfig';
import { formatBytes } from '../utils/formatter';

export type ToolStatus = 'idle' | 'loading' | 'success' | 'error';

export interface OutputFile {
    name: string;
    data: Uint8Array;
    size: number;
}

interface ToolActionAreaProps {
    status: string; //ToolStatus;
    onAction: () => void;
    onReset: () => void;
    onDownloadAll?: () => void;
    onDownloadSingle?: (fileName: string) => void;
    onPreviewOutput?: (fileName: string) => void;
    actionButtonText: string;
    successTitle: string;
    successMessage: string;
    errorMessage: string;
    outputFiles?: OutputFile[];
    disabled?: boolean;
    children?: ReactNode; // NEW: Add children prop
}

const ToolActionArea: React.FC<ToolActionAreaProps> = ({
                                                           status,
                                                           onAction,
                                                           onReset,
                                                           onDownloadAll,
                                                           onDownloadSingle,
                                                           onPreviewOutput,
                                                           actionButtonText,
                                                           successTitle,
                                                           successMessage,
                                                           errorMessage,
                                                           outputFiles = [],
                                                           disabled = false,
                                                           children, // NEW: Destructure children
                                                       }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [showFileList, setShowFileList] = useState(false);
    const [nextTool, setNextTool] = useState('');

    const handleNextToolNavigation = () => {
        if(nextTool) {
            navigate(nextTool);
        }
    };

    const renderContent = () => {
        switch (status) {
            case 'loading':
                return (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
                        <CircularProgress sx={{ mr: 2 }}/>
                        <Typography>{t('processing', { ns: 'tools' })}</Typography>
                    </Box>
                );

            case 'success':
                const isSingleFile = outputFiles.length === 1;
                const totalSize = outputFiles.reduce((acc, file) => acc + file.size, 0);
                const formattedSize = formatBytes(totalSize);
                const fileType = isSingleFile ? 'PDF' : 'ZIP';
                const mainButtonText = isSingleFile ? t('download_file', { ns: 'tools' }) : t('download_all_zip', { ns: 'tools' });

                return (
                    <Paper elevation={0} sx={{ p: 3, bgcolor: 'success.lightest', border: `1px solid ${theme.palette.success.main}` }}>
                        <Stack spacing={2} alignItems="center">
                            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 40 }}/>
                            <Typography variant="h5" component="p">{successTitle}</Typography>
                            <Typography color="text.secondary">{successMessage}</Typography>

                            {/* NEW: Render children here, this is where the result summary will go */}
                            {children && <Box sx={{ width: '100%', my: 2 }}>{children}</Box>}

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2, width: '100%', justifyContent: 'center' }}>
                                {onDownloadAll && (
                                    <Button variant="contained" startIcon={<DownloadIcon />} onClick={onDownloadAll}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', ml: 1, textTransform: 'none' }}>
                                            <Typography variant="button" sx={{ lineHeight: 1.2 }}>
                                                {mainButtonText}
                                            </Typography>
                                            <Typography variant="caption" sx={{ opacity: 0.8, lineHeight: 1.2 }}>
                                                {`${fileType} (${formattedSize})`}
                                            </Typography>
                                        </Box>
                                    </Button>
                                )}
                                {outputFiles.length === 1 && onPreviewOutput && (
                                    <Button variant="outlined" startIcon={<VisibilityIcon />} onClick={() => onPreviewOutput(outputFiles[0].name)}>
                                        {t('preview', { ns: 'tools' })}
                                    </Button>
                                )}
                                <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={onAction}>
                                    {actionButtonText}
                                </Button>
                                <Button variant="text" startIcon={<RestartAltIcon />} onClick={onReset}>
                                    {t('process_more_files', { ns: 'tools' })}
                                </Button>
                            </Stack>

                            {outputFiles.length > 1 && (
                                <Button fullWidth onClick={() => setShowFileList(!showFileList)} endIcon={showFileList ? <ExpandLessIcon /> : <ExpandMoreIcon />} sx={{ mt: 2 }}>
                                    {t('show_individual_files', { count: outputFiles.length, ns: 'tools' })}
                                </Button>
                            )}
                            <Collapse in={showFileList} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
                                <List dense>
                                    {outputFiles.map(file => (
                                        <ListItemButton
                                            key={file.name}
                                            onClick={() => onPreviewOutput && onPreviewOutput(file.name)}
                                        >
                                            <ListItemIcon><PictureAsPdfIcon /></ListItemIcon>
                                            <ListItemText primary={file.name} secondary={formatBytes(file.size)} />
                                            <IconButton edge="end" onClick={(e) => { e.stopPropagation(); onDownloadSingle && onDownloadSingle(file.name); }}>
                                                <DownloadIcon />
                                            </IconButton>
                                        </ListItemButton>
                                    ))}
                                </List>
                            </Collapse>

                            <Stack direction="row" spacing={2} sx={{ mt: 4, width: '100%', maxWidth: 400 }} alignItems="center">
                                <FormControl fullWidth size="small">
                                    <InputLabel id="next-tool-label">{t('continue_with_another_tool', { ns: 'tools' } )}</InputLabel>
                                    <Select
                                        labelId="next-tool-label"
                                        value={nextTool}
                                        label={t('continue_with_another_tool', { ns: 'tools' } )}
                                        onChange={(e) => setNextTool(e.target.value)}
                                    >
                                        {searchableTools.map(tool => (
                                            <MenuItem key={tool.id} value={tool.route}>{t(tool.nameKey, { ns: 'tools' })}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Button variant="outlined" onClick={handleNextToolNavigation} disabled={!nextTool}>{t('go', { ns: 'tools' })}</Button>
                            </Stack>
                        </Stack>
                    </Paper>
                );

            case 'error':
                return (
                    <Alert severity="error" icon={<ErrorOutlineIcon />} sx={{ width: '100%', p:3 }}>
                        <Typography variant="h6" gutterBottom>{t('error_title', { ns: 'tools' })}!</Typography>
                        <Typography sx={{mb: 2}}>{errorMessage}</Typography>
                        <Stack direction="row" spacing={2}>
                            <Button variant="outlined" color="error" startIcon={<RestartAltIcon />} onClick={onAction}>
                                {actionButtonText}
                            </Button>
                            <Button variant="outlined" color="error" startIcon={<RestartAltIcon />} onClick={onReset}>
                                {t('reset', { ns: 'tools' })}
                            </Button>
                        </Stack>
                    </Alert>
                );

            case 'idle':
            default:
                return (
                    <Stack direction="row" spacing={2} sx={{ width: '100%', justifyContent: 'center' }}>
                        <LoadingButton
                            variant="contained"
                            size="large"
                            onClick={onAction}
                            loading={status === 'loading'}
                            disabled={disabled || status === 'loading'}
                        >
                            {actionButtonText}
                        </LoadingButton>
                        <Button variant="text" onClick={onReset} disabled={status === 'loading'}>
                            {t('reset', { ns: 'tools' })}
                        </Button>
                    </Stack>
                );
        }
    };

    return (
        <Box sx={{ width: '100%', maxWidth: '900px', mt: 4 }}>
            {renderContent()}
        </Box>
    );
};

export default ToolActionArea;